import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Play, ArrowUpRight, AtSign, Globe, Rss } from 'lucide-react'
import logo from '@/assets/logo-mark-v3.png'
import logoWordmarkWhite from '@/assets/logo-wordmark-white.png'
import { useBookCategories } from '@/services/bookService'
import { useAuthStore } from '@/store/authStore'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
}

// คอลัมน์ คอร์สเรียน/หนังสือ/หางาน เป็นภาษาไทย — คอลัมน์ "หนังสือ" ต่อท้ายด้วยหมวดหมู่หนังสือจาก API (ใส่ใน Footer ตอน render)
const links: Record<string, { label: string; href: string }[]> = {
  คอร์สเรียน: [
    { label: 'คอร์สทั้งหมด', href: '/courses' },
    { label: 'หลักสูตรอบรม', href: '/trainings' },
    { label: 'เรียนฟรี', href: '/courses?free=true' },
    { label: 'คอร์สขายดี', href: '/courses?sort=popular' },
  ],
  หนังสือ: [
    { label: 'หนังสือทั้งหมด', href: '/books' },
  ],
  หางาน: [
    { label: 'ตำแหน่งงานทั้งหมด', href: '/jobs' },
    { label: 'งานทำงานทางไกล', href: '/jobs?remote=true' },
    { label: 'งานด่วน', href: '/jobs?urgent=true' },
    { label: 'สำหรับผู้ประกอบการ', href: '/employers' },
  ],
}

// Support อยู่ใต้ข้อความแนะนำแบรนด์ (คอลัมน์ซ้าย) — ไม่ใช่คอลัมน์ลิงก์ฝั่งขวา
const supportLinks = [
    { label: 'ศูนย์ช่วยเหลือ', href: '/help' },
    { label: 'ประกาศความเป็นส่วนตัว', href: '/privacy' },
    { label: 'นโยบายความเป็นส่วนตัว', href: '/privacy-policy' },
    { label: 'ข้อตกลงการใช้งาน', href: '/terms' },
    { label: 'คืนเงิน', href: '/refund' },
]

const socials = [
  { icon: Play, href: '#', label: 'YouTube' },
  { icon: AtSign, href: '#', label: 'X / Twitter' },
  { icon: Globe, href: '#', label: 'Instagram' },
  { icon: Rss, href: '#', label: 'LinkedIn' },
]

function FooterSection({ section, items }: { section: string; items: { label: string; href: string }[] }) {
  return (
    <div>
      <h3 className="mb-4 text-sm font-semibold text-white">{section}</h3>
      <ul className="space-y-2.5">
        {items.map((item) => (
          <li key={item.label}>
            <Link to={item.href} className="text-sm text-white/50 hover:text-white transition-colors">
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function Footer() {
  // ลิงก์ "สำหรับผู้ประกอบการ" เห็นเฉพาะบัญชีบริษัท — คนอื่นซ่อน
  const isCompany = useAuthStore((s) => s.isLoggedIn && s.customerType === 'COMPANY')
  const bookCategories = useBookCategories()
  const visibleLinks = Object.fromEntries(
    Object.entries(links).map(([section, items]) => {
      const all = section === 'หนังสือ'
        ? [...items, ...bookCategories.map((c) => ({ label: c.name, href: `/books?cat=${c.category_id}` }))]
        : items
      return [section, all.filter((i) => i.href !== '/employers' || isCompany)]
    }),
  )

  // แจกหัวข้อลง 2 คอลัมน์ตามจำนวนรายการ (นับหัวข้อเป็น 1 แถว) ให้สองฝั่งสูงใกล้กันที่สุด
  const mobileColumns: [string, { label: string; href: string }[]][][] = [[], []]
  const heights = [0, 0]
  for (const entry of Object.entries(visibleLinks) as [string, { label: string; href: string }[]][]) {
    const col = heights[0] <= heights[1] ? 0 : 1
    mobileColumns[col].push(entry)
    heights[col] += entry[1].length + 1
  }

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-[#1E1836] via-[#171129] to-[#100D1C]">
      {/* Soft glow accents */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/4 top-0 h-72 w-72 -translate-y-1/2 rounded-full bg-accent/20 blur-[110px]" />
        <div className="absolute right-1/4 bottom-0 h-72 w-72 translate-y-1/3 rounded-full bg-accentBlue/10 blur-[110px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          transition={{ staggerChildren: 0.1 }}
          className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-5 md:gap-12"
        >
          {/* Brand */}
          <motion.div
            variants={fadeUp}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="col-span-2"
          >
            <Link to="/" className="flex flex-col items-start gap-2 mb-4">
              <img src={logo} alt="Jos CoWork" className="h-16 w-auto object-contain" />
              <img src={logoWordmarkWhite} alt="JosCoWork" className="h-6 w-auto object-contain" />
            </Link>
            <p className="text-sm leading-relaxed text-white/50 max-w-xs">
              แพลตฟอร์มเรียนออนไลน์ที่ครอบคลุมที่สุด ด้วยผู้สอนระดับอุตสาหกรรม คอร์สอัปเดตตลอดเวลา
            </p>
            <div className="mt-6 flex gap-3">
              {socials.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-white/50 hover:border-accent2/50 hover:text-white hover:-translate-y-0.5 transition-all"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>

            <div className="mt-10">
              <h3 className="mb-4 text-sm font-semibold text-white">Support</h3>
              <ul className="space-y-2.5">
                {supportLinks.map((item) => (
                  <li key={item.label}>
                    <Link to={item.href} className="text-sm text-white/50 hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Links — มือถือ: แบ่ง 2 คอลัมน์ความสูงใกล้กัน (ใส่หัวข้อถัดไปลงคอลัมน์ที่สั้นกว่า) ไม่ใช้ grid แถวเดียวกัน
              เพราะแถวบนจะสูงตามคอลัมน์ "หนังสือ" ที่ยาว ทำให้หัวข้อถัดไปลอยห่างลงไป / จอใหญ่: คอลัมน์ละหัวข้อเหมือนเดิม */}
          <div className="col-span-2 grid grid-cols-2 gap-x-6 md:hidden">
            {mobileColumns.map((col, i) => (
              <div key={i} className="space-y-10">
                {col.map(([section, items]) => (
                  <FooterSection key={section} section={section} items={items} />
                ))}
              </div>
            ))}
          </div>
          {Object.entries(visibleLinks).map(([section, items]) => (
            <motion.div key={section} variants={fadeUp} transition={{ duration: 0.5, ease: 'easeOut' }} className="hidden md:block">
              <FooterSection section={section} items={items} />
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row"
        >
          <p className="text-xs text-white/40">© 2026 JosCoWork. All rights reserved.</p>
          <a
            href="#"
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors"
          >
            Made with ❤️ in Thailand
            <ArrowUpRight className="h-3 w-3" />
          </a>
        </motion.div>
      </div>
    </footer>
  )
}
